import { Component, Prop, h, Host, Fragment, Element, State } from '@stencil/core';
import { getEventDate, isSameDay, isSameYear } from './DateService';
import {
  CalendarEvent,
  createGoogleCalendarLink,
  createICSDataLink,
  toSnakeCase,
} from './CalendarService';
import { EventDate } from './EventDate';
import { getWhatsAppLink, isWebShareSupported, openWebShare } from './ShareService';

@Component({
  tag: 'chapel-event',
  styleUrl: 'chapel-event.scss',
  shadow: false,
})
export class ChapelEvent {

  @Prop() start: string;
  @Prop() end: string;

  @Prop() name?: string;
  @Prop() description?: string;

  @Prop() address1?: string;
  @Prop() address2?: string;
  @Prop() address3?: string;

  @Prop() map?: string;
  @Prop() url?: string;
  @Prop() file?: string;
  @Prop() color?: string = '000000';

  @State() showCalendarShareOptions = false;
  @State() isFetchingShare = false;

  @Element() host: HTMLElement;

  connectedCallback() {
    this.host.innerText = '';
  }

  render() {
    const { color } = this;
    const start = getEventDate(this.start);
    const end = getEventDate(this.end);
    const isSameDayEvent = isSameDay(start, end);
    const isSameYearEvent = isSameYear(start, end);

    const addressLines = (
      [this.address1, this.address2, this.address3]
        .map(address => address?.trim())
        .filter(Boolean)
    );

    const event: CalendarEvent = {
      startDate: this.start,
      endDate: this.end,
      name: this.name,
      description: this.description?.trim(),
      addressLines
    };

    const icsLink = createICSDataLink(event);
    const googleCalendarLink = createGoogleCalendarLink(event);
    const canUseWebShare = isWebShareSupported();
    const shouldUseWebShare = Boolean(start && end && canUseWebShare);
    const shouldUseWhatsapp = Boolean(start && end && !canUseWebShare);

    const openShare = async () => {
      this.isFetchingShare = true;
      await openWebShare(event, start, end, this.url, this.file);
      this.isFetchingShare = false;
    };

    const openGoogleLink = () => {
      const w = window.open('');
      setTimeout(() => {
        w.location = googleCalendarLink;
      }, 1000);
    }

    return (
      <Host>
        {this.start && (
          <div class="row">
            <loadable-image src={getColoredIcon('https://img.icons8.com/?size=64&id=16152&format=png', color)} />
            <EventDate
              date={start}
              endTime={isSameDayEvent ? end.time : undefined}
              showYear={!isSameYearEvent}
            />
            {this.end && !isSameDayEvent && (
              <Fragment>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>&mdash;</span>
                </div>
                <EventDate
                  date={end}
                  showYear={!isSameYearEvent}
                />
              </Fragment>
            )}
          </div>
        )}

        {addressLines.length > 0 && (
          <div class="row">
            <loadable-image src={getColoredIcon('https://img.icons8.com/?size=100&id=2436&format=png', color)} />
            <div>
              {addressLines.map(line => <div>{line}</div>)}
            </div>
          </div>
        )}

        <div class='chapel-event-sharing'>
          {this.start && (
            <div onClick={() => this.showCalendarShareOptions = !this.showCalendarShareOptions}>
              <loadable-image src={getColoredIcon('https://img.icons8.com/?size=48&id=XXf3LGliTTWQ&format=png', color)} />
              <span>Add</span>
            </div>
          )}
          {this.map && (
            <a href={this.map} target="_blank">
              <loadable-image src={getColoredIcon('https://img.icons8.com/?size=96&id=85961&format=png', color)} />
              <span>Directions</span>
            </a>
          )}
          {shouldUseWebShare && (
            <div onClick={openShare}>
              <loadable-image src={getColoredIcon('https://img.icons8.com/?size=100&id=11504&format=png', color)} />
              {!this.isFetchingShare && <span>Share</span>}
              {this.isFetchingShare && <span>Loading...</span>}
            </div>
          )}
          {shouldUseWhatsapp && (
            <a href={getWhatsAppLink(event, start, end, this.url)} target="_blank">
              <loadable-image src={getColoredIcon('https://img.icons8.com/?size=96&id=85088&format=png', color)} />
              <span>Invite</span>
            </a>
          )}
        </div>

        {this.showCalendarShareOptions && (
          <div class='chapel-event-sharing chapel-event-sharing-calendars'>
            <a href={icsLink} download={toSnakeCase(this.name)} target="_blank">
              <loadable-image src={getColoredIcon('https://img.icons8.com/?size=120&id=95294&format=png', color)} />
              <span>Apple</span>
            </a>
            <div onClick={openGoogleLink}>
              <img src={getColoredIcon('https://img.icons8.com/?size=100&id=UJGaYqXaHWdp&format=png', color)} />
              <span>Google</span>
            </div>
          </div>
        )}
      </Host>
    );
  }

}

function getColoredIcon(iconUrl: string, color: string): string {
  return `${iconUrl}&color=${color}`;
}
